/**
 * RatingScatter — one-dot-per-rated-trace scatter chart (IA §8 charts row,
 * Plan 04-09 Task 2).
 *
 * Recharts `<ScatterChart>` with:
 *   - X axis: time (ISO string → ms)
 *   - Y axis: rating 1..5 (discrete ticks)
 *   - Dot colour: red for 1-2★, amber for 3★, green for 4-5★
 *   - Click dot → open Langfuse trace in a new tab (same VITE_LANGFUSE_BASE
 *     pattern as FeedbackCard / UserActivityFeed)
 */

import {
  CartesianGrid,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';

import {
  TOOLTIP_CLASSNAME,
  useChartTooltipTrigger,
} from '@/components/charts/ChartTooltip';
import { chartPalette } from '@/lib/chart-palette';
import { formatDateShort, formatDateTimeFull } from '@/lib/format-date';
import { formatRating } from '@/lib/format';
import type { UserRatingDot } from '@/services/api-types';

// Langfuse base — mirrors FeedbackCard / UserActivityFeed.
const LANGFUSE_TRACE_BASE = (
  import.meta.env.VITE_LANGFUSE_BASE ??
  'https://langfuse.botsfleet.online/trace/'
).replace(/\/?$/, '/');

// ---------------------------------------------------------------------------
// Colour rule — matches the QualityPage palette semantics (red bad / green
// good) so colour meaning is consistent across tabs.
// ---------------------------------------------------------------------------

// Scatter uses a 3-band semantic split (bad/warn/good) instead of the full
// 5-stop rating scale because overlapping dots on small charts read better
// with 3 distinct colours than 5 close neighbours. Palette SSOT keeps the
// "good = green / bad = red / warn = amber" meaning consistent.
function colourForRating(rating: 1 | 2 | 3 | 4 | 5): string {
  if (rating <= 2) return chartPalette.negative;
  if (rating === 3) return chartPalette.warning;
  return chartPalette.positive;
}

// ---------------------------------------------------------------------------
// Data shape — Recharts expects numeric axes, so pre-compute timestamp (ms).
// ---------------------------------------------------------------------------

interface ScatterPoint {
  t: number;
  rating: 1 | 2 | 3 | 4 | 5;
  traceId: string;
  iso: string;
}

function formatAxisTick(t: number): string {
  return formatDateShort(t);
}

// Tooltip receives the Recharts payload array; we know the shape because
// we control the data. Cast through `unknown` to keep TS strict mode happy.
function ScatterTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload?: ScatterPoint }>;
}): React.ReactElement | null {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload;
  if (!p) return null;
  return (
    <div
      data-testid="rating-scatter-tooltip"
      className={TOOLTIP_CLASSNAME}
    >
      <div>
        <span className="font-mono">{p.traceId}</span>
      </div>
      <div>
        <span className="text-muted-foreground">When: </span>
        {formatDateTimeFull(p.iso)}
      </div>
      <div>
        <span className="text-muted-foreground">Rating: </span>
        <span className="font-medium">{formatRating(p.rating)}</span>
      </div>
      <div className="mt-1 text-[10px] text-muted-foreground">
        click → open trace
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface RatingScatterProps {
  dots: UserRatingDot[];
  height?: number;
}

export function RatingScatter({
  dots,
  height = 240,
}: RatingScatterProps): React.ReactElement {
  // Mobile tap persistence — see ChartTooltip.useChartTooltipTrigger.
  const tooltipTrigger = useChartTooltipTrigger();
  if (!dots || dots.length === 0) {
    return (
      <div
        data-testid="rating-scatter-empty"
        className="flex items-center justify-center w-full text-sm text-muted-foreground"
        style={{ height }}
      >
        No rated traces in selected period
      </div>
    );
  }

  // Group by rating bucket so each <Scatter> series gets one fill colour
  // — Recharts can't colour per-dot via `fill` on the point level without
  // a custom `<Shape>` renderer, and per-series fills are simpler and
  // still match our 3-tier red/amber/green palette.
  const byColour: Record<string, ScatterPoint[]> = {
    [chartPalette.negative]: [],
    [chartPalette.warning]: [],
    [chartPalette.positive]: [],
  };
  for (const d of dots) {
    const p: ScatterPoint = {
      t: Date.parse(d.timestamp),
      rating: d.rating,
      traceId: d.traceId,
      iso: d.timestamp,
    };
    if (!Number.isNaN(p.t)) byColour[colourForRating(d.rating)]!.push(p);
  }

  const handleClick = (point: ScatterPoint): void => {
    if (!point.traceId) return;
    if (typeof window !== 'undefined') {
      window.open(
        `${LANGFUSE_TRACE_BASE}${point.traceId}`,
        '_blank',
        'noopener,noreferrer',
      );
    }
  };

  return (
    <div data-testid="rating-scatter" style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height={height}>
        <ScatterChart margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            type="number"
            dataKey="t"
            domain={['dataMin', 'dataMax']}
            tickFormatter={formatAxisTick}
            // Treat as a UTC day scale — aligns with the charts row's
            // other series (requests/cost trends) which bucket by UTC day.
            scale="time"
          />
          <YAxis
            type="number"
            dataKey="rating"
            domain={[0.5, 5.5]}
            ticks={[1, 2, 3, 4, 5]}
            allowDecimals={false}
          />
          <ZAxis range={[60, 60]} />
          <Tooltip
            {...tooltipTrigger}
            content={<ScatterTooltip />}
            cursor={{ strokeDasharray: '3 3' }}
          />
          {(Object.keys(byColour) as Array<keyof typeof byColour>).map(
            (fill) => {
              const series = byColour[fill];
              if (!series || series.length === 0) return null;
              return (
                <Scatter
                  key={fill}
                  data={series}
                  fill={fill}
                  isAnimationActive={false}
                  onClick={(p: unknown) =>
                    handleClick((p as { payload: ScatterPoint }).payload)
                  }
                  // Cursor change so users see dots are clickable.
                  style={{ cursor: 'pointer' }}
                />
              );
            },
          )}
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}
